import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient, userApi } from '@/api'
import type { User } from '@/api'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { useAnalytics } from '@/composables/useAnalytics'

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<User | null>(null)
  const isLoadingUser = ref(false)
  const isInitialized = ref(false)

  /** Try to load the current user from the server (cookie-based auth) */
  async function fetchCurrentUser() {
    isLoadingUser.value = true
    try {
      const user = await userApi.getCurrentUser()
      currentUser.value = user
      return user
    } catch (e) {
      console.error('Failed to fetch current user:', e)
      currentUser.value = null
      return null
    } finally {
      isLoadingUser.value = false
    }
  }

  /**
   * Initialize auth state on app load.
   * Tries GET /me — if the httpOnly cookie is valid, we're authenticated.
   * If the access token has expired but the refresh token is still valid,
   * the response interceptor will transparently refresh and retry.
n   *
   * Uses promise coalescing so concurrent calls (e.g. multiple router guard
   * invocations) share a single /me request.
   */
  let initPromise: Promise<void> | null = null

  async function init() {
    if (isInitialized.value) return
    if (initPromise) return initPromise

    initPromise = (async () => {
      isLoadingUser.value = true
      try {
        const response = await apiClient.get<User>('/me')
        currentUser.value = response.data
        // Identify returning user for analytics + error tracking (UUID only — no PII)
        if (currentUser.value?.id) {
          const { identify, capture } = useAnalytics()
          identify(currentUser.value.id)
          capture('session_started', { role: currentUser.value.role?.toLowerCase(), platform: 'web' })
          Sentry.setUser({ id: currentUser.value.id })
        }
      } catch {
        currentUser.value = null
      } finally {
        isLoadingUser.value = false
        isInitialized.value = true
        initPromise = null
      }
    })()

    return initPromise
  }

  /**
   * Called after a successful login/register/google auth.
   * Tokens are already set in cookies by the server — just load the user.
   */
  async function onAuthenticated() {
    await fetchCurrentUser()
    // Identify user for analytics + error tracking (UUID only — no PII)
    if (currentUser.value?.id) {
      const { identify } = useAnalytics()
      identify(currentUser.value.id)
      Sentry.setUser({ id: currentUser.value.id })
    }
  }

  /** Clear local state. Cookie clearing is handled by the server on /auth/logout. */
  function logout() {
    currentUser.value = null
    isInitialized.value = false
    const { reset } = useAnalytics()
    reset()
    Sentry.setUser(null)
  }

  /** Auth is determined by whether we have a server-verified user */
  const isAuthenticated = computed(() => !!currentUser.value)

  const userName = computed(() => {
    if (currentUser.value?.name) {
      return currentUser.value.name
    }
    // Fallback to email-derived name
    const email = currentUser.value?.email
    if (email?.includes('@')) {
      const name = email.split('@')[0]
      return name ? name.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'User'
    }
    return 'User'
  })

  /** Whether the current user is a student */
  const isStudent = computed(() => {
    const role = currentUser.value?.role as string | undefined
    return role === 'Student' || role === 'Parent'
  })

  /** Whether the current user is a teacher (Teacher or Admin) */
  const isTeacher = computed(() => {
    const role = currentUser.value?.role as string | undefined
    return role === 'Teacher' || role === 'Admin' || !role
  })

  /** Check if the user needs to complete onboarding (no tenant assigned) */
  const needsOnboarding = computed(() => {
    if (!currentUser.value) return false
    // Students/parents don't go through onboarding — they access data via student record linkage
    if (isStudent.value) return false
    return !currentUser.value.tenantId
  })

  /** Check if user has completed onboarding */
  const hasCompletedOnboarding = computed(() => {
    if (!currentUser.value) return false
    if (isStudent.value) return true
    return !!currentUser.value.tenantId
  })

  /** Whether the user has an active Pro subscription */
  const isPro = computed(() => !!currentUser.value?.isPremium)

  /**
   * Trigger a cookie-based token refresh.
   * The refresh token is in an httpOnly cookie — server reads it automatically.
   * Used after onboarding to get a new JWT with tenant_id claim.
   */
  async function refreshAccessToken() {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    await axios.post(`${baseUrl}/auth/refresh`, {}, {
      withCredentials: true,
      headers: { 'X-Client-Type': 'web' },
    })
  }

  /** Update current user in store (e.g., after profile edit) */
  function setUser(user: User) {
    currentUser.value = user
  }

  return {
    currentUser,
    isLoadingUser,
    isInitialized,
    userName,
    isStudent,
    isTeacher,
    needsOnboarding,
    hasCompletedOnboarding,
    isPro,
    onAuthenticated,
    init,
    logout,
    setUser,
    fetchCurrentUser,
    refreshAccessToken,
    isAuthenticated,
  }
})
